import { useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { GoogleLogo, MicrosoftOutlookLogo, ArrowsClockwise, Plugs } from "@phosphor-icons/react";
import { CardShell, AppNotConfigured, fmtWhen } from "./shared";

export default function CalendarCard({ item, onChange }) {
  const provider = item?.provider;
  const conn = item?.connection;
  const [busy, setBusy] = useState("");
  const isGoogle = provider === "google_calendar";
  const icon = isGoogle ? <GoogleLogo size={20} weight="bold" className="text-red-500" /> : <MicrosoftOutlookLogo size={20} weight="duotone" className="text-blue-600" />;

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await API.post(`/integrations/${provider}/connect`, { back: "/settings?tab=integrations" });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start sign-in"); setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${item.name}? Fixlr events already in your calendar are left as they are.`)) return;
    setBusy("disconnect");
    try { await API.post(`/integrations/${provider}/disconnect`); toast.success(`${item.name} disconnected`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
    finally { setBusy(""); }
  };

  const sync = async () => {
    setBusy("sync");
    try { const { data } = await API.post(`/integrations/${provider}/sync`); toast.success(`Pushed ${data.pushed} visits · ${data.jobs_updated} job${data.jobs_updated === 1 ? "" : "s"} updated from your calendar`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(""); }
  };

  const toggle = async (k, v) => {
    try { await API.put(`/integrations/${provider}/settings`, { [k]: v }); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <CardShell title={item?.name} icon={icon} item={item} testid={`integration-${provider}`}
      right={item?.team_connected > 0 && <span className="text-[10px] text-muted-foreground" data-testid={`${provider}-team-count`}>{item.team_connected} on your team</span>}>
      {!conn ? (
        <>
          <AppNotConfigured item={item} testid={`${provider}-app-missing`} />
          <Button size="sm" onClick={connect} disabled={busy === "connect" || !item?.app?.configured} className="gap-1" data-testid={`${provider}-connect-btn`}>
            <Plugs size={14} /> {busy === "connect" ? "Opening…" : `Connect ${isGoogle ? "Google" : "Microsoft"}`}
          </Button>
        </>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" data-testid={`${provider}-account`}>Signed in as <b className="text-foreground">{conn.account?.email}</b> · last sync {fmtWhen(conn.last_sync_at)}</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center justify-between gap-2 text-xs rounded border border-border px-2 py-1.5"><span>Push visits</span><Switch checked={conn.settings?.push !== false} onCheckedChange={(v) => toggle("push", v)} data-testid={`${provider}-toggle-push`} /></label>
            <label className="flex items-center justify-between gap-2 text-xs rounded border border-border px-2 py-1.5"><span>Pull changes</span><Switch checked={conn.settings?.pull !== false} onCheckedChange={(v) => toggle("pull", v)} data-testid={`${provider}-toggle-pull`} /></label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={sync} disabled={!!busy} className="gap-1" data-testid={`${provider}-sync-btn`}><ArrowsClockwise size={13} className={busy === "sync" ? "animate-spin" : ""} /> Sync now</Button>
            <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={disconnect} disabled={!!busy} data-testid={`${provider}-disconnect-btn`}>Disconnect</Button>
          </div>
        </div>
      )}
    </CardShell>
  );
}
